/**
 * /careers — Careers page.
 *
 * Honest "not currently hiring publicly" page with a role-alerts signup and
 * a note on what we look for. Replaces the Phase 9 ComingSoonShell stub per
 * Phase 3 Task 3.7 of the 2026-05-16 implementation plan.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/careers')({
  component: CareersPage,
});

function CareersPage() {
  useEffect(() => {
    document.title = 'Careers · Elreign: fiduciary, cryptography, product engineering';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Elreign is not hiring publicly today. Subscribe to role alerts if your background is fiduciary practice, cryptography, or premium B2B product engineering.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 56 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Careers</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(36px, 5.5vw, 56px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            We are not hiring publicly <em>today.</em>
          </h1>
          <p style={{ fontSize: 19, color: 'var(--ink-3)', lineHeight: 1.65, margin: 0 }}>
            We open roles infrequently and fill them through our network. When a role does open,
            we publish it here and notify the alert list first. If that suits how you look for
            work, subscribe to the alert list below.
          </p>
        </header>

        {/* Alert signup */}
        <section
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-xl)',
            padding: '32px 32px 28px',
            marginBottom: 56,
          }}
        >
          <h2
            className="serif"
            style={{
              fontSize: 22,
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 14px',
              letterSpacing: '-0.01em',
            }}
          >
            Role alerts
          </h2>
          <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0 }}>
            We do not run an automated alert list yet. Write to{' '}
            <a
              href="mailto:careers@elreign.io?subject=Role%20alerts"
              style={{ color: 'var(--primary-600)' }}
            >
              careers@elreign.io
            </a>{' '}
            with &quot;Role alerts&quot; in the subject and we will email you directly when we
            next open a role in the areas described below.
          </p>
        </section>

        {/* What we hire for */}
        <section>
          <h2
            className="serif"
            style={{
              fontSize: 26,
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 20px',
              letterSpacing: '-0.01em',
            }}
          >
            What we hire for
          </h2>
          <p style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 28 }}>
            We are small and intend to stay small. When we hire, it is from one of three
            backgrounds. We do not consider general applications outside these areas.
          </p>
          <Lane
            heading="Fiduciary practice"
            body="Senior trust officers, trust accountants, and fiduciary practitioners with at least seven years of South African inter-generational trust experience. You will work on product, not on customer trusts. The job is to be the voice in the room when a feature decision touches the deed, the resolution, or the Master."
          />
          <Lane
            heading="Cryptography and security"
            body="Engineers with applied cryptography experience: hash chains, key management, secure-enclave-backed signing, threshold schemes. We are particularly interested in candidates who have shipped to a regulated industry (financial services, healthcare, defence) and understand audit posture as a deliverable, not a check-list."
          />
          <Lane
            heading="Product engineering"
            body="Full-stack engineers with React, TypeScript, and a serverless backend (we use Azure Functions on Node 20). The bar is product judgement, not framework familiarity. You read the spec, ask the question that catches the bug at design time, and ship code that reads as cleanly as the brief."
          />
        </section>

        {/* Footer note */}
        <p
          style={{
            marginTop: 56,
            paddingTop: 24,
            borderTop: '1px solid var(--hairline)',
            fontSize: 14,
            color: 'var(--ink-3)',
            lineHeight: 1.6,
          }}
        >
          If your background is in one of the three areas above and the timing is right for you,
          you can write directly to{' '}
          <a href="mailto:careers@elreign.io" style={{ color: 'var(--primary-600)' }}>
            careers@elreign.io
          </a>{' '}
          with a short note. We answer every message.
        </p>
      </div>
    </MarketingLayout>
  );
}

interface LaneProps {
  heading: string;
  body: string;
}

function Lane({ heading, body }: LaneProps) {
  return (
    <div
      style={{
        paddingTop: 22,
        marginTop: 22,
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <h3
        style={{
          fontSize: 17,
          fontWeight: 600,
          color: 'var(--ink)',
          margin: '0 0 10px',
          letterSpacing: '-0.01em',
        }}
      >
        {heading}
      </h3>
      <p style={{ fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.65, margin: 0 }}>{body}</p>
    </div>
  );
}
